import {getCookie} from '@github-ui/cookies'
import type {ErrorContext} from '@github-ui/failbot'
// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {sendStats} from '@github-ui/stats'
import type {ErrorInfo} from 'react'
import type {HydrationOptions, RootOptions} from 'react-dom/client'

import {ERROR_BOUNDARY_METADATA, type ErrorBoundaryMetadata} from './ErrorBoundary'
import {isDevelopmentOrStaffUser} from './is-development-or-is-staff-user'

/**
 * Options for creating React root error handlers.
 */
export interface ReactRootErrorHandlerOptions {
  /**
   * The name of the React application, used for error reporting context.
   *
   * Pass a string for static apps that mount one shell. Pass a function for apps
   * (such as the TanStack Start UI service) where the active app changes per route;
   * the function will be called at error time so the most current name is reported.
   */
  appName: string | (() => string | undefined)
  /**
   * Optional callback invoked whenever a recoverable error is classified as
   * hydration-related.
   */
  onHydrationError?: (details: RecoverableErrorDetails) => void
  /**
   * Optional callback invoked for every React recoverable error, including
   * recoveries unrelated to hydration.
   */
  onRecoverableError?: (details: RecoverableErrorDetails) => void
}

export type RecoverableErrorKind = 'hydration' | 'render' | 'unknown'
export type RecoverableErrorReason =
  | 'content-mismatch'
  | 'incomplete-suspense-boundary'
  | 'early-update'
  | 'hydration-exception'
  | 'concurrent-render-exception'
  | 'unknown'
export type RecoverableErrorScope = 'boundary' | 'root' | 'tree-unknown' | 'unknown'

export interface RecoverableErrorDetails {
  error: unknown
  invariant: string
  kind: RecoverableErrorKind
  reason: RecoverableErrorReason
  scope: RecoverableErrorScope
  guidance: string
  cause?: unknown
  componentStack?: string
}

function resolveAppName(appName: ReactRootErrorHandlerOptions['appName']): string | undefined {
  return typeof appName === 'function' ? appName() : appName
}

/**
 * Props that may be present on an error boundary component.
 * This is a subset of ErrorBoundaryProps that doesn't require children,
 * since React's errorInfo.errorBoundary.props type doesn't include children.
 */
interface ErrorBoundaryLikeProps {
  critical?: boolean
  boundaryName?: string
  onError?: (error: Error, context?: ErrorContext) => void
  /** TanStack Router CatchBoundary has an onCatch instead of onError */
  onCatch?: (error: Error, context?: ErrorInfo) => void
}

/**
 * The error info object passed to React 19's error callbacks.
 * This type is compatible with React's internal typing while allowing
 * access to our ErrorBoundary-specific props.
 */
export interface ReactErrorInfo {
  componentStack?: string | null
  errorBoundary?: {
    props?: ErrorBoundaryLikeProps
    constructor?: {
      name?: string
    }
  }
}

const REACT_INVARIANT_ERROR_REGEX = /Minified React error #(?<invariant>\d+)/

type RecoverableErrorMetadata = Omit<RecoverableErrorDetails, 'error' | 'invariant' | 'cause' | 'componentStack'> & {
  messageFragments: readonly string[]
}
type RecoverableErrorClassification = RecoverableErrorMetadata & Pick<RecoverableErrorDetails, 'invariant'>

// After recovery commits, React replaces the affected React-owned SSR DOM; it
// does not intentionally retain that DOM and commit a second client tree beside
// or underneath it. If both remain, look for separate or repeated root mounts,
// markup outside the recovered root/boundary, or imperative/custom-element DOM
// that React does not own. Server rendering and the initial client render can
// both execute component/module/data work, but only the successful client tree
// commits React effects and handlers. Replacement can still lose native DOM state.

const RECOVERABLE_ERROR_CLASSIFICATIONS = {
  // The server and client trees differ. React regenerates the nearest Suspense
  // tree, or the entire root when there is no boundary. Fix nondeterministic
  // rendering, missing data snapshots, invalid HTML, or pre-hydration DOM changes.
  '418': {
    kind: 'hydration',
    reason: 'content-mismatch',
    scope: 'tree-unknown',
    guidance: 'Make the server and first client render deterministic and structurally identical.',
    messageFragments: ['Hydration failed because the server rendered'],
  },
  // The server emitted an unfinished or errored Suspense boundary. React
  // preserves the rest of the root but client-renders this boundary. Ensure the
  // boundary can finish during SSR or intentionally render a stable fallback.
  '419': {
    kind: 'hydration',
    reason: 'incomplete-suspense-boundary',
    scope: 'boundary',
    guidance: 'Ensure this Suspense boundary can finish during SSR or intentionally render a stable fallback.',
    messageFragments: ['The server could not finish this Suspense boundary'],
  },
  // Legacy React path: an update reached a dehydrated Suspense boundary and
  // forced boundary client rendering. Wrap non-urgent updates in startTransition
  // and avoid eager updates before hydration. React 19.2.8 has no emitting call
  // site, but older or cached bundles can still report this invariant.
  '421': {
    kind: 'hydration',
    reason: 'early-update',
    scope: 'boundary',
    guidance: 'Wrap non-urgent updates in startTransition and avoid updating the boundary before hydration.',
    messageFragments: ['This Suspense boundary received an update before it finished hydrating'],
  },
  // A component threw while hydrating inside Suspense. React client-renders the
  // boundary. Inspect error.cause; the wrapper only describes the recovery.
  '422': {
    kind: 'hydration',
    reason: 'hydration-exception',
    scope: 'boundary',
    guidance: 'Inspect the error cause and fix the exception thrown while hydrating this Suspense boundary.',
    messageFragments: [
      'There was an error while hydrating but React was able to recover by instead client rendering from the nearest Suspense boundary',
      'There was an error while hydrating this Suspense boundary',
    ],
  },
  // A component threw while hydrating outside Suspense. React abandons hydration
  // for the entire root. Fix error.cause; adding a boundary can reduce future
  // blast radius but does not fix the underlying exception.
  '423': {
    kind: 'hydration',
    reason: 'hydration-exception',
    scope: 'root',
    guidance: 'Inspect the error cause and fix the exception thrown while hydrating the root.',
    messageFragments: [
      'There was an error while hydrating but React was able to recover by instead client rendering the entire root',
      'There was an error while hydrating. Because the error happened outside of a Suspense boundary',
    ],
  },
  // root.render ran before initial hydration committed. React clears the SSR DOM
  // and client-renders the whole root. Avoid root updates during startup and keep
  // initialization inside the initial hydrateRoot tree.
  '424': {
    kind: 'hydration',
    reason: 'early-update',
    scope: 'root',
    guidance: 'Do not update the root before initial hydration completes.',
    messageFragments: ['This root received an early update, before anything was able hydrate'],
  },
  // Not a hydration failure. A concurrent render threw, then React rebuilt the
  // root synchronously and succeeded. Render-phase work may repeat, but only the
  // successful tree commits DOM and effects. Fix the exception in error.cause.
  '520': {
    kind: 'render',
    reason: 'concurrent-render-exception',
    scope: 'root',
    guidance: 'Inspect the error cause and fix the exception that interrupted concurrent rendering.',
    messageFragments: [
      'There was an error during concurrent rendering but React was able to recover by instead synchronously rendering the entire root',
    ],
  },
} as const satisfies Record<string, RecoverableErrorMetadata>

function classifyRecoverableError(error: unknown): RecoverableErrorClassification {
  if (!(error instanceof Error)) {
    return {
      invariant: 'non-error',
      kind: 'unknown',
      reason: 'unknown',
      scope: 'unknown',
      guidance: 'Investigate why React received a non-Error recoverable value.',
      messageFragments: [],
    }
  }

  const invariant = REACT_INVARIANT_ERROR_REGEX.exec(error.message)?.groups?.invariant
  const invariantClassification = invariant
    ? (RECOVERABLE_ERROR_CLASSIFICATIONS as Record<string, RecoverableErrorMetadata>)[invariant]
    : undefined
  if (invariant && invariantClassification) return {...invariantClassification, invariant}

  // During development, React preserves a server Suspense exception's original
  // message and moves its digest onto the Error instead of using the standard
  // invariant 419 message.
  if ('digest' in error) return {...RECOVERABLE_ERROR_CLASSIFICATIONS['419'], invariant: '419'}

  for (const [mappedInvariant, classification] of Object.entries(RECOVERABLE_ERROR_CLASSIFICATIONS)) {
    if (classification.messageFragments.some(fragment => error.message.includes(fragment))) {
      return {...classification, invariant: mappedInvariant}
    }
  }

  return {
    invariant: invariant ?? 'unknown',
    kind: 'unknown',
    reason: 'unknown',
    scope: 'unknown',
    guidance: 'Inspect the error and component stack to classify this recoverable error.',
    messageFragments: [],
  }
}

/**
 * Creates the `onCaughtError` callback for React 19's `createRoot`/`hydrateRoot`.
 *
 * This callback is invoked when an error is caught by an Error Boundary.
 * It handles reporting to Sentry while respecting:
 * - Errors with `shouldSkipReport` flag (e.g., ES being down)
 * - Error Boundaries with custom `onError` handlers (they handle their own reporting)
 * - Critical vs non-critical error classification
 *
 * @param options - Configuration for the error handler
 * @returns A callback suitable for React 19's `onCaughtError` option
 *
 * @example
 * ```ts
 * import { createOnCaughtError, createOnUncaughtError } from '@github-ui/react-core/react-root-error-handlers'
 *
 * const root = createRoot(container, {
 *   onCaughtError: createOnCaughtError({ appName: 'my-app' }),
 *   onUncaughtError: createOnUncaughtError({ appName: 'my-app' }),
 * })
 * ```
 */
export function createOnCaughtError(options: ReactRootErrorHandlerOptions) {
  const reportedBoundaryErrors = new WeakMap<object, Map<string | undefined, boolean>>()

  return (error: unknown, errorInfo: ReactErrorInfo) => {
    const appName = resolveAppName(options.appName)

    // Try to read props from the error boundary instance
    // Note: errorInfo.errorBoundary is the React component instance
    const boundaryProps = errorInfo.errorBoundary?.props

    // Also check for metadata attached to the error (fallback for componentDidCatch)
    const errorMetadata = (error as Error & {[ERROR_BOUNDARY_METADATA]?: ErrorBoundaryMetadata})[
      ERROR_BOUNDARY_METADATA
    ]

    // Prefer props from boundary instance, fall back to error metadata
    const critical = boundaryProps?.critical ?? errorMetadata?.critical ?? false
    const boundaryName =
      boundaryProps?.boundaryName ?? errorMetadata?.boundaryName ?? errorInfo.errorBoundary?.constructor?.name
    const hasOnError = typeof boundaryProps?.onError === 'function'
    const hasOnCatch = typeof boundaryProps?.onCatch === 'function'

    // Log in development or production for a staff user so caught errors are
    // still visible even when we skip Sentry reporting.
    if (isDevelopmentOrStaffUser()) {
      // eslint-disable-next-line no-console
      console.error('Error caught by boundary:', error)
      if (errorInfo.componentStack) {
        // eslint-disable-next-line no-console
        console.warn('componentStack', errorInfo.componentStack)
      }
    }

    // Skip reporting if:
    // 1. Error has shouldSkipReport flag (e.g., ES being down)
    // 2. Boundary has a custom onError handler (it handles reporting)
    // 3. Boundary has a custom onCatch handler (for TanStack Router — it handles its own reporting)
    // 4. This root already reported the same error for this app at equal or higher severity
    if (error && typeof error === 'object' && 'shouldSkipReport' in error && error.shouldSkipReport) {
      return
    }
    if (hasOnError || hasOnCatch) {
      return
    }
    if (error && typeof error === 'object') {
      const reportedApps = reportedBoundaryErrors.get(error) ?? new Map<string | undefined, boolean>()
      const reportedCritical = reportedApps.get(appName)
      if (reportedCritical === true || (reportedCritical === false && !critical)) {
        return
      }
      reportedApps.set(appName, critical)
      reportedBoundaryErrors.set(error, reportedApps)
    }

    reportError(error, {
      critical,
      reactAppName: appName,
      reactErrorBoundaryName: boundaryName,
    })
  }
}

/**
 * Creates the `onUncaughtError` callback for React 19's `createRoot`/`hydrateRoot`.
 *
 * This callback is invoked when an error is thrown and NOT caught by any Error Boundary.
 * These are critical errors that will crash the app.
 *
 * @param options - Configuration for the error handler
 * @returns A callback suitable for React 19's `onUncaughtError` option
 *
 * @example
 * ```ts
 * import { createOnCaughtError, createOnUncaughtError } from '@github-ui/react-core/react-root-error-handlers'
 *
 * const root = createRoot(container, {
 *   onCaughtError: createOnCaughtError({ appName: 'my-app' }),
 *   onUncaughtError: createOnUncaughtError({ appName: 'my-app' }),
 * })
 * ```
 */
export function createOnUncaughtError(options: ReactRootErrorHandlerOptions) {
  return (error: unknown, errorInfo: ReactErrorInfo) => {
    const appName = resolveAppName(options.appName)

    // This is called when an error is thrown and NOT caught by any Error Boundary.
    // These are critical errors that will crash the app.
    reportError(error, {
      critical: true,
      reactAppName: appName,
    })

    // Log in development or production for a staff user
    if (isDevelopmentOrStaffUser()) {
      // eslint-disable-next-line no-console
      console.error('Error not caught by boundary:', error)
      if (errorInfo.componentStack) {
        // eslint-disable-next-line no-console
        console.warn('componentStack', errorInfo.componentStack)
      }
    }
  }
}

function createOnRecoverableError(options: ReactRootErrorHandlerOptions) {
  return (error: unknown, errorInfo: ReactErrorInfo) => {
    const appName = resolveAppName(options.appName)

    const {messageFragments: _messageFragments, ...classification} = classifyRecoverableError(error)
    const details = {
      ...classification,
      error,
      cause: error instanceof Error ? error.cause : undefined,
      componentStack: errorInfo.componentStack ?? undefined,
    } satisfies RecoverableErrorDetails

    const incrementTags = {
      appName,
      invariant: details.invariant,
      kind: details.kind,
      reason: details.reason,
      scope: details.scope,
    }
    sendStats(
      {
        incrementKey: 'REACT_RECOVERABLE_ERROR',
        incrementTags,
        requestUrl: window.location.href,
      },
      false,
      1,
    )

    if (details.kind === 'hydration') {
      sendStats(
        {
          incrementKey: 'REACT_HYDRATION_ERROR',
          incrementTags,
          requestUrl: window.location.href,
        },
        false,
        1,
      )
      options.onHydrationError?.(details)
    }
    options.onRecoverableError?.(details)

    if (!(error instanceof Error)) {
      if (isDevelopmentOrStaffUser()) {
        // eslint-disable-next-line no-console
        console.warn(`Recoverable error (non-Error value) - ${appName}:`, error)
      }
      return
    }

    /** Log recoverable errors in development or production for a staff user */
    if (!isDevelopmentOrStaffUser()) return
    // eslint-disable-next-line no-console
    console.groupCollapsed(
      `%cRecoverable ${details.kind} error - ${appName} - ${error.message}`,
      'background: rgba(100, 149, 237, 0.2); font-weight: bold; padding: 4px; border: 1px solid rgba(100, 149, 237, 0.5); border-radius: 4px;',
      `${details.guidance} Recovery scope: ${details.scope}.`,
    )
    if (details.kind === 'hydration' && process.env.NODE_ENV === 'development') {
      const isHydrationScanningEnabled = getCookie('hydrationScan')?.value !== 'false'
      if (isHydrationScanningEnabled) {
        const errorContainer = document.querySelector<HTMLElement>('#hydration-error-notice')
        const errorCounter = errorContainer?.querySelector<HTMLElement>('.js-hydration-error-count')
        if (errorContainer && errorCounter) {
          errorCounter.textContent = (parseInt(errorCounter.textContent || '0', 10) + 1).toString()
          errorContainer.hidden = false
        }
      }
    }
    if (error.cause) {
      // eslint-disable-next-line no-console
      console.warn('cause', error.cause)
    }
    if (errorInfo.componentStack) {
      // eslint-disable-next-line no-console
      console.warn('componentStack', errorInfo.componentStack)
    }
    // eslint-disable-next-line no-console
    console.groupEnd()
  }
}

/**
 * Creates React 19 error callbacks for `createRoot`/`hydrateRoot`.
 *
 * This is a convenience function that returns `onCaughtError`, `onUncaughtError`,
 * and `onRecoverableError` callbacks configured for the given app name.
 *
 * @param options - Configuration for the error handlers
 * @returns An object containing all three React root error callbacks
 *
 * @example
 * ```ts
 * import { createReactRootErrorHandlers } from '@github-ui/react-core/react-root-error-handlers'
 *
 * const root = createRoot(
 *   container,
 *   createReactRootErrorHandlers({ appName: 'my-app' }),
 * )
 * ```
 *
 * Create a fresh handler object for every root. The caught-error deduplication
 * state is scoped to the returned handler object.
 */
export function createReactRootErrorHandlers(options: ReactRootErrorHandlerOptions) {
  return {
    onCaughtError: createOnCaughtError(options),
    onUncaughtError: createOnUncaughtError(options),
    onRecoverableError: createOnRecoverableError(options),
  } satisfies HydrationOptions & RootOptions
}
